// チェックシート: 「何をチェックしているか」をカタログ全項目で一覧するドメイン非依存プリミティブ。
// 診断結果(pass/fail/na/review)に加え、診断対象外(not-evaluated)と未実施(not-run)を区別して表示する
// （未確認を合格と偽らないため）。SF / GW 双方のチェックシート画面が本プリミティブを共有する。
// 重大度の**表記**はドメインごとに異なる(SF=「重大」/ GW=「致命的」)ので props.severityLabels で受け取り、
// ここで文言を決め打ちしない（同一診断内で他タブと表記が食い違うのを防ぐ）。
import { createFilterableTable, type FtColumn, type FilterDef } from "./filterable-table";
import { createDrawer } from "./drawer";
import { createBadge } from "./badge";
import { createSeverityBadge, SEVERITY_LEVELS, SEVERITY_ORDER, type SeverityLevel } from "./severity-badge";
import { downloadCsv, escapeCsvField } from "./csv";
import { TONE_CLASSES, type Tone } from "./tokens";

export type ChecksheetStatus = "pass" | "fail" | "na" | "review" | "not-evaluated" | "not-run";

export type ChecksheetSeverityLabels = Record<SeverityLevel, string>;

export interface ChecksheetCategory {
  readonly key: string;
  readonly label: string;
}

export interface ChecksheetRow {
  readonly checkId: string;
  readonly category: string;
  readonly categoryLabel: string;
  readonly title: string;
  readonly what: string;
  readonly why: string;
  readonly severity: SeverityLevel;
  readonly remediation: string;
  readonly status: ChecksheetStatus;
  readonly affectedCount: number;
  readonly affectedResources: readonly string[];
}

// 行ビルダーが受け取るカタログ項目 / 診断結果の最小形。
// SF の Check・GW の GwCheck、SF/GW の Finding はいずれもこの形を満たすので、
// ドメイン側はカタログと文言を渡すだけで済む（両ドメインで突き合わせロジックを二重に持たない）。
export interface ChecksheetCatalogItem {
  readonly id: string;
  readonly category: string;
  readonly title: string;
  readonly what: string;
  readonly why: string;
  readonly severity: SeverityLevel;
  readonly remediation: string;
}

export interface ChecksheetFinding {
  readonly check: { readonly id: string };
  readonly status: "pass" | "fail" | "na" | "review";
  readonly affectedCount: number;
  readonly affectedResources: readonly string[];
}

export interface ChecksheetRowsInput {
  readonly checks: readonly ChecksheetCatalogItem[];
  readonly categories: readonly ChecksheetCategory[];
  // 診断結果。**未注入(undefined)は「診断未実行」**で全項目 not-run（未実施）。
  // **空配列は「診断は実行したが結果 0 件」**で全項目 not-evaluated（未評価）。
  // どちらも「確認できていない」が原因が違うので潰さない（?? [] にすると未実施が消える）。
  readonly findings?: readonly ChecksheetFinding[];
}

// カタログ全項目を必ず 1 行ずつ返す（診断結果のある項目だけを出すと「見ていない項目」が消える）。
// バックエンドの coverage.notEvaluated は「カタログ − evaluated」の補集合なので、
// 「findings に無い = 未評価」で同じ結論になる。別引数で受け取っても行は変わらないため受け取らない。
export function buildChecksheetRows(input: ChecksheetRowsInput): ChecksheetRow[] {
  const byId = new Map((input.findings ?? []).map((f) => [f.check.id, f] as const));
  const notRun = input.findings === undefined;
  // カテゴリラベルは 1 つの Map から引く（行ごとの線形探索を避け、表のフィルタと解決経路を揃える）。
  const categoryLabels = new Map(input.categories.map((c) => [c.key, c.label] as const));

  return input.checks.map((c): ChecksheetRow => {
    const f = byId.get(c.id);
    return {
      checkId: c.id,
      category: c.category,
      categoryLabel: categoryLabels.get(c.category) ?? c.category,
      title: c.title,
      what: c.what,
      why: c.why,
      severity: c.severity,
      remediation: c.remediation,
      status: notRun ? "not-run" : (f?.status ?? "not-evaluated"),
      affectedCount: f?.affectedCount ?? 0,
      affectedResources: f?.affectedResources ?? [],
    };
  });
}

export const CHECKSHEET_STATUS_LABEL: Record<ChecksheetStatus, string> = {
  pass: "合格",
  fail: "要対応",
  na: "対象外",
  review: "要確認",
  "not-evaluated": "未評価",
  "not-run": "未実施",
};

const STATUS_TONE: Record<ChecksheetStatus, Tone> = {
  pass: "success",
  fail: "danger",
  na: "neutral",
  review: "warning",
  "not-evaluated": "neutral",
  "not-run": "neutral",
};

// CSV は「生キー列 + 日本語ラベル列」を併記する。
// 生キーがあれば findingsToCsv 等の既存エクスポートと category/severity/status で結合でき、
// ラベル列があれば表計算でそのまま読める（どちらか一方だと必ずどちらかの用途が壊れる）。
const CSV_HEADER = [
  "checkId",
  "category",
  "categoryLabel",
  "title",
  "severity",
  "severityLabel",
  "status",
  "statusLabel",
  "affectedCount",
  "affectedResources",
  "what",
  "why",
  "remediation",
];

// 影響リソースの区切り。CSV のセル内で複数値を持たせるため、カンマではなく ; を使う。
const AFFECTED_SEPARATOR = ";";

// 未実施・未評価はそもそも数えていないので、影響数を 0 と表示しない
// （「確認した結果 0 件」と誤読されるため）。表では「—」、CSV では空セルにする。
function isCounted(status: ChecksheetStatus): boolean {
  return status !== "not-run" && status !== "not-evaluated";
}

export function formatAffectedCount(row: ChecksheetRow): string {
  return isCounted(row.status) ? String(row.affectedCount) : "—";
}

export function checksheetToCsv(rows: readonly ChecksheetRow[], severityLabels: ChecksheetSeverityLabels): string {
  const body = rows.map((r) =>
    [
      r.checkId,
      r.category,
      r.categoryLabel,
      r.title,
      r.severity,
      severityLabels[r.severity],
      r.status,
      CHECKSHEET_STATUS_LABEL[r.status],
      isCounted(r.status) ? String(r.affectedCount) : "",
      r.affectedResources.join(AFFECTED_SEPARATOR),
      r.what,
      r.why,
      r.remediation,
    ]
      .map(escapeCsvField)
      .join(","),
  );
  return [CSV_HEADER.join(","), ...body].join("\n") + "\n";
}

function detailSection(label: string, body: string): HTMLElement {
  const s = document.createElement("section");
  const h = document.createElement("h3");
  h.className = "text-sm font-medium text-slate-700";
  h.textContent = label;
  const p = document.createElement("p");
  p.className = "mt-1 text-sm text-slate-700";
  p.textContent = body;
  s.append(h, p);
  return s;
}

function createDetail(r: ChecksheetRow, severityLabels: ChecksheetSeverityLabels): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "space-y-4";
  const head = document.createElement("div");
  head.className = "flex flex-wrap items-center gap-2";
  head.append(
    createSeverityBadge({ severity: r.severity, label: severityLabels[r.severity] }),
    createBadge({ label: CHECKSHEET_STATUS_LABEL[r.status], tone: STATUS_TONE[r.status] }),
  );
  const id = document.createElement("p");
  id.className = "text-xs text-slate-500";
  id.textContent = "チェック ID: " + r.checkId;
  wrap.append(
    head,
    id,
    detailSection("何を確認するか", r.what),
    detailSection("なぜ重要か", r.why),
    detailSection("推奨対応", r.remediation),
  );
  if (r.affectedResources.length > 0) {
    const s = document.createElement("section");
    const h = document.createElement("h3");
    h.className = "text-sm font-medium text-slate-700";
    h.textContent = "影響（" + String(r.affectedCount) + "件）";
    const ul = document.createElement("ul");
    ul.className = "mt-1 list-disc pl-5 text-sm text-slate-700";
    const SHOWN = 50;
    for (const v of r.affectedResources.slice(0, SHOWN)) {
      const li = document.createElement("li");
      li.textContent = v;
      ul.append(li);
    }
    s.append(h, ul);
    // 50件超は黙って切り詰めず残数を明示する。
    if (r.affectedResources.length > SHOWN) {
      const more = document.createElement("p");
      more.className = "mt-1 text-xs text-slate-500";
      more.textContent = "ほか " + String(r.affectedResources.length - SHOWN) + " 件";
      s.append(more);
    }
    wrap.append(s);
  }
  return wrap;
}

export interface ChecksheetProps {
  readonly caption: string;
  readonly rows: readonly ChecksheetRow[];
  readonly categories: readonly ChecksheetCategory[];
  // 重大度の表記はドメインの用語に合わせる（SF=「重大」/ GW=「致命的」）。
  readonly severityLabels: ChecksheetSeverityLabels;
  readonly csvFilename: string;
}

export function createChecksheet(props: ChecksheetProps): HTMLElement {
  const root = document.createElement("div");

  // 集計サマリ: 全ステータスを個別のチップにして、チップの合計 = 全項目数 になるようにする
  // （対象外を落としたり、未評価と未実施を合算したりすると、行の表示と数が食い違う）。
  const count = (s: ChecksheetStatus): number => props.rows.filter((r) => r.status === s).length;
  const summary = document.createElement("div");
  summary.dataset["testid"] = "checksheet-summary";
  summary.className = "mb-4 flex flex-wrap gap-3";
  const chips: readonly (readonly [string, number])[] = [
    ["全項目", props.rows.length],
    ["要対応", count("fail")],
    ["要確認", count("review")],
    ["合格", count("pass")],
    ["対象外", count("na")],
    ["未評価", count("not-evaluated")],
    ["未実施", count("not-run")],
  ];
  for (const [label, value] of chips) {
    const chip = document.createElement("div");
    chip.className = "rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700";
    chip.textContent = label + " " + String(value) + " 件";
    summary.append(chip);
  }

  const drawer = createDrawer({ title: "チェック詳細" });

  const columns: FtColumn<ChecksheetRow>[] = [
    {
      key: "severity",
      header: "重大度",
      cell: (r) => createSeverityBadge({ severity: r.severity, label: props.severityLabels[r.severity] }),
      sortable: true,
      sortAccessor: (r) => SEVERITY_ORDER[r.severity],
    },
    {
      key: "category",
      header: "カテゴリ",
      cell: (r) => r.categoryLabel,
      sortable: true,
      sortAccessor: (r) => r.category,
    },
    {
      key: "title",
      header: "チェック項目",
      cell: (r) => r.title,
      sortable: true,
      sortAccessor: (r) => r.title,
    },
    { key: "what", header: "何を確認するか", cell: (r) => r.what },
    {
      key: "status",
      header: "結果",
      cell: (r) => createBadge({ label: CHECKSHEET_STATUS_LABEL[r.status], tone: STATUS_TONE[r.status] }),
    },
    {
      key: "affectedCount",
      header: "影響数",
      cell: (r) => formatAffectedCount(r),
      sortable: true,
      // 未確認（—）は数値の 0 と並べず末尾に沈める（影響の大きい順に見たいのが主目的）。
      sortAccessor: (r) => (isCounted(r.status) ? r.affectedCount : -1),
    },
    { key: "remediation", header: "推奨対応", cell: (r) => r.remediation },
  ];

  const filters: FilterDef<ChecksheetRow>[] = [
    {
      id: "category",
      label: "カテゴリ",
      options: props.categories.map((c) => ({ value: c.key, label: c.label })),
      accessor: (r) => r.category,
    },
    {
      id: "severity",
      label: "重大度",
      options: SEVERITY_LEVELS.map((s) => ({ value: s, label: props.severityLabels[s] })),
      accessor: (r) => r.severity,
    },
    // チェックシートは「全項目の一覧」が目的なので既定フィルタは設けない（defaultValue なし）。
    {
      id: "status",
      label: "結果",
      options: (Object.keys(CHECKSHEET_STATUS_LABEL) as ChecksheetStatus[]).map((s) => ({
        value: s,
        label: CHECKSHEET_STATUS_LABEL[s],
      })),
      accessor: (r) => r.status,
    },
  ];

  // CSV は「表示中（絞り込み後）の行」を出す。53 項目のシートで 要対応 に絞った利用者は
  // その分だけを期待するため。何件出るかはボタン自身に明示する。
  let visible: readonly ChecksheetRow[] = props.rows;
  const bar = document.createElement("div");
  bar.className = "mb-2 flex justify-end";
  const csvBtn = document.createElement("button");
  csvBtn.type = "button";
  csvBtn.className = "rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100";
  const setCsvLabel = (): void => {
    csvBtn.textContent = "CSV 出力（表示中 " + String(visible.length) + " 件）";
  };
  setCsvLabel();
  csvBtn.addEventListener("click", () =>
    downloadCsv(props.csvFilename, checksheetToCsv(visible, props.severityLabels)),
  );
  bar.append(csvBtn);

  const table = createFilterableTable<ChecksheetRow>({
    caption: props.caption,
    columns,
    rows: props.rows,
    filters,
    searchable: true,
    searchAccessor: (r) => r.title + " " + r.what,
    onRowClick: (r) => {
      drawer.setTitle(r.title);
      drawer.setContent(createDetail(r, props.severityLabels));
      drawer.open();
    },
    onVisibleRowsChange: (rows) => {
      visible = rows;
      setCsvLabel();
    },
    emptyMessage: "該当するチェック項目はありません。",
    rowTestId: "checksheet-row",
  });

  root.append(summary, bar, table, drawer.element);
  return root;
}

export interface ChecksheetScreenProps extends ChecksheetProps {
  // 画面冒頭の説明文（対象プロダクト名が入るのでドメインが持つ）。
  readonly intro: string;
  // 全項目が未実施のときに出す「次の一手」。接続タブの案内文がドメインごとに違うので受け取る。
  readonly notRunNotice: string;
}

// チェックシート画面 1 枚分（説明文 + 未実施の案内 + 表）を描画する。
// SF / GW の差分はカタログと文言だけなので、画面の骨格はここに置く。
export function renderChecksheet(root: HTMLElement, props: ChecksheetScreenProps): void {
  root.replaceChildren();

  const intro = document.createElement("p");
  intro.className = "mb-4 text-sm text-slate-600";
  intro.textContent = props.intro;
  root.append(intro);

  // 全項目が未実施＝診断が一度も走っていない状態。一覧だけ見せて放置せず、次に何をすればよいか示す。
  if (props.rows.length > 0 && props.rows.every((r) => r.status === "not-run")) {
    const notice = document.createElement("p");
    notice.dataset["testid"] = "checksheet-not-run-notice";
    notice.className = "mb-4 rounded border px-3 py-2 text-sm " + TONE_CLASSES.warning.bar;
    notice.textContent = props.notRunNotice;
    root.append(notice);
  }

  root.append(createChecksheet(props));
}
